// =============================================================================
// SaaS-Admin › Usage tracking
// Live data from GET /saastracker/admin/usage?days=N.
//
// The page is split by SCOPE, because mixing the two is how a dashboard starts
// lying: everything above the window control is all-time (a prepaid wallet is
// not a monthly quota — lifetime utilisation is what says who is about to run
// dry), and everything below it re-renders against the selected window.
// =============================================================================
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as api from '../services/adminDataService';
import { RankedBars, TrendArea, TrendPoint } from '../charts/Charts';

const WINDOWS = [7, 30, 90];

// ---- formatting -------------------------------------------------------------

const fmtCredits = (n: number | null | undefined): string =>
  (n ?? 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

/** Axis ticks want compact round numbers, not two decimals. */
const fmtAxis = (n: number): string =>
  n >= 1000 ? `${(n / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}k`
            : n.toLocaleString('en-US', { maximumFractionDigits: 1 });

const fmtInt = (n: number | null | undefined): string => (n ?? 0).toLocaleString('en-US');

const dayLabel = (iso: string, full: boolean): string => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return isNaN(dt.getTime())
    ? iso
    : dt.toLocaleDateString('en-US', full
        ? { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }
        : { month: 'short', day: 'numeric' });
};

const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * The API only returns days that saw activity, so a raw plot would compress the
 * gaps and misstate the trend. Fill the window with zeros — and union in any key
 * the server returned outside it, since it groups by the stored UTC offset while
 * we build the range locally, which can differ by a day at the edges.
 */
function densify(rows: api.AdminUsageDayRow[], days: number): TrendPoint[] {
  const byKey = new Map(rows.map(r => [r.day, r]));
  const keys = new Set<string>();

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    keys.add(isoDay(d));
  }
  rows.forEach(r => keys.add(r.day));

  // ISO keys sort chronologically as plain strings.
  return Array.from(keys).sort().map(key => {
    const r = byKey.get(key);
    return {
      key,
      label: dayLabel(key, false),
      fullLabel: dayLabel(key, true),
      value: r?.credits ?? 0,
      count: r?.count ?? 0,
    };
  });
}

/**
 * The bar encodes utilisation and nothing else — the same thresholds the app's
 * own banner uses (90% critical, 75% low). Lifecycle is the status pill's job:
 * folding it in here painted a suspended tenant at 24% used the same red as one
 * that had actually run dry.
 */
const barClass = (pctUsed: number): string =>
  pctUsed >= 90 ? ' danger' : pctUsed >= 75 ? ' warn' : '';

// ---- page -------------------------------------------------------------------

export const UsageTracking: React.FC = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<api.AdminUsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showAllTenants, setShowAllTenants] = useState(false);
  const [tab, setTab] = useState<'alltime' | 'activity'>('alltime');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      setData(await api.getUsage(days));
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const series = useMemo(
    () => (data ? densify(data.byDay, data.days) : []),
    [data],
  );

  const actionRows = useMemo(
    () => (data?.byAction ?? []).slice(0, 12).map(a => ({
      key: a.actionKey,
      label: a.displayName || a.actionKey,
      sublabel: a.module && a.module !== '—' ? a.module : undefined,
      value: a.credits,
      count: a.count,
    })),
    [data],
  );

  const tenants = data?.tenants ?? [];
  const visibleTenants = showAllTenants ? tenants : tenants.slice(0, 20);

  return (
    <div className="ov-page">
      <header className="ov-header">
        <div>
          <h1 className="ov-header__title">Usage tracking</h1>
          <p className="ov-header__sub">
            Credit consumption across every tenant, straight from the ledger.
          </p>
        </div>
        <div className="ov-header__actions">
          <button className="btn-small" onClick={load} disabled={loading}>
            <span className="material-icons">refresh</span> Refresh
          </button>
        </div>
      </header>

      {err && (
        <div style={{
          margin: '0 0 14px', padding: '9px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
          background: '#fdecea', color: '#c0392b', border: '1px solid #f5c6cb',
        }}>
          {err}
          <button className="btn-small" style={{ marginLeft: 10 }} onClick={load}>Retry</button>
        </div>
      )}

      {!data && loading && (
        <div className="empty">
          <span className="material-icons">hourglass_empty</span>
          Loading usage…
        </div>
      )}

      {data && (
        <>
          {/* The two scopes are separate tabs rather than one long page, so a
              lifetime figure can never be mistaken for a windowed one. Both come
              from the same response — switching tabs costs no request. */}
          <nav className="rem-tabs" role="tablist" aria-label="Usage scope">
            <button
              role="tab" id="usage-tab-alltime" aria-controls="usage-panel-alltime"
              aria-selected={tab === 'alltime'}
              className={`rem-tab${tab === 'alltime' ? ' rem-tab--active' : ''}`}
              onClick={() => setTab('alltime')}
            >
              All time
            </button>
            <button
              role="tab" id="usage-tab-activity" aria-controls="usage-panel-activity"
              aria-selected={tab === 'activity'}
              className={`rem-tab${tab === 'activity' ? ' rem-tab--active' : ''}`}
              onClick={() => setTab('activity')}
            >
              Activity
            </button>
          </nav>

          {tab === 'alltime' && (
            <div role="tabpanel" id="usage-panel-alltime" aria-labelledby="usage-tab-alltime">
              <p className="viz-tab-note">
                Lifetime totals across the credit ledger. A prepaid wallet is not a monthly
                quota, so these are the figures that say who is close to running dry.
              </p>

              <section className="ov-kpis">
                <Kpi icon="battery_full" tone="green"
                     num={fmtCredits(data.lifetimeCreditsGranted)}
                     label="Credits granted" sub="free credit + purchases" />
                <Kpi icon="bolt" tone="blue"
                     num={fmtCredits(data.lifetimeCreditsUsed)}
                     label="Credits consumed" sub={`across ${fmtInt(tenants.length)} tenants`} />
                <Kpi icon="data_usage" tone="purple"
                     num={`${data.lifetimeAverageUtilization}%`}
                     label="Average utilisation" sub="tenants with credit granted" />
                <Kpi icon="warning" tone="warn"
                     num={fmtInt(tenants.filter(t => t.pctUsed >= 80).length)}
                     label="Nearing their limit" sub="80% or more consumed" />
              </section>

              <section className="ov-section">
                <div className="ov-section__head">
                  <h2 className="ov-section__title">Tenant utilisation — highest first</h2>
                  {tenants.length > 20 && (
                    <button className="btn-small btn-small--ghost" onClick={() => setShowAllTenants(s => !s)}>
                      {showAllTenants ? 'Show top 20' : `Show all ${fmtInt(tenants.length)}`}
                    </button>
                  )}
                </div>

                {tenants.length === 0 ? (
                  <div className="viz-empty">No tenants yet.</div>
                ) : (
                  <div className="usage-list">
                    {visibleTenants.map((t, idx) => (
                      <div className="usage-row" key={t.id}>
                        <div className="usage-row__rank">{idx + 1}</div>
                        <div className="usage-row__main">
                          <div className="usage-row__name">
                            {t.name}
                            <span className={`plan-tag plan-tag--${t.plan ? t.plan.toLowerCase().replace(/[^a-z0-9]+/g, '-') : 'none'}`}>
                              {t.plan ? t.plan.toUpperCase() : 'NONE'}
                            </span>
                            <span className={`status-pill status-pill--${t.status.toLowerCase()}`}>{t.status}</span>
                          </div>
                          <div className="usage-row__domain">{t.email || 'no email on file'}</div>
                        </div>
                        <div className="usage-row__bar">
                          <div className="usage-row__bar-track">
                            <div
                              className={`usage-row__bar-fill${barClass(t.pctUsed)}`}
                              style={{ width: `${Math.min(100, Math.max(0, t.pctUsed))}%` }}
                            />
                          </div>
                          <div className="usage-row__numbers">
                            {fmtCredits(t.totalUsed)} / {fmtCredits(t.totalGranted)} credits ·{' '}
                            <strong>{t.pctUsed}%</strong> · {fmtCredits(t.balance)} left
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}

          {tab === 'activity' && (
            <div role="tabpanel" id="usage-panel-activity" aria-labelledby="usage-tab-activity">
              {/* One filter row, above everything it scopes. */}
              <div className="viz-filters">
                <span className="viz-filters__label">Activity window</span>
                <div className="viz-presets" role="group" aria-label="Activity window">
                  {WINDOWS.map(w => (
                    <button
                      key={w}
                      className={`viz-preset${days === w ? ' viz-preset--on' : ''}`}
                      aria-pressed={days === w}
                      onClick={() => setDays(w)}
                    >
                      Last {w} days
                    </button>
                  ))}
                </div>
                <span className="viz-filters__note">Everything below is scoped to this range.</span>
              </div>

              {/* Refetch holds the previous render rather than flashing a skeleton. */}
              <div className="viz-scope" style={{ opacity: loading ? 0.45 : 1 }}>
                <section className="ov-kpis">
                  <Kpi icon="bolt" tone="blue"
                       num={fmtCredits(data.creditsUsedInWindow)}
                       label="Credits consumed" sub={`in the last ${data.days} days`} />
                  <Kpi icon="touch_app" tone="purple"
                       num={fmtInt(data.actionsInWindow)}
                       label="Metered actions" sub={`in the last ${data.days} days`} />
                  <Kpi icon="trending_up" tone="green"
                       num={fmtInt(data.topUpsInWindow)}
                       label="Top-ups completed" sub={`in the last ${data.days} days`} />
                </section>

                <VizCard
                  title="Credits consumed per day"
                  sub={`Daily burn over the last ${data.days} days. Peak is labelled; hover or use arrow keys for any day.`}
                  table={
                    <VizTable
                      head={['Day', 'Credits', 'Actions']}
                      rows={series.map(p => [p.fullLabel, fmtCredits(p.value), fmtInt(p.count)])}
                    />
                  }
                >
                  <TrendArea
                    data={series}
                    formatValue={fmtAxis}
                    valueLabel="credits"
                    countLabel="actions"
                  />
                </VizCard>

                <VizCard
                  title="Credits by action"
                  sub={
                    (data.byAction.length > 12 ? `Top 12 of ${data.byAction.length} actions. ` : '') +
                    'Which billable operations are actually spending the credit.'
                  }
                  table={
                    <VizTable
                      head={['Action', 'Module', 'Credits', 'Times run']}
                      rows={data.byAction.map(a => [
                        a.displayName || a.actionKey, a.module, fmtCredits(a.credits), fmtInt(a.count),
                      ])}
                    />
                  }
                >
                  <RankedBars
                    rows={actionRows}
                    formatValue={fmtCredits}
                    countLabel="runs"
                    emptyText="No metered actions in this window."
                  />
                </VizCard>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---- small pieces -----------------------------------------------------------

const Kpi: React.FC<{
  icon: string; tone: 'blue' | 'green' | 'purple' | 'warn';
  num: string; label: string; sub: string;
}> = ({ icon, tone, num, label, sub }) => (
  <div className="kpi">
    <div className={`kpi__icon kpi__icon--${tone}`}><span className="material-icons">{icon}</span></div>
    <div className="kpi__main">
      <div className="kpi__num">{num}</div>
      <div className="kpi__label">{label}</div>
      <div className="kpi__sub">{sub}</div>
    </div>
  </div>
);

/** Every chart ships with a table twin, so no value is reachable only by hovering. */
const VizCard: React.FC<{
  title: string;
  sub: string;
  table: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, sub, table, children }) => {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  return (
    <section className="viz-card">
      <div className="viz-card__head">
        <div>
          <h2 className="viz-card__title">{title}</h2>
          <p className="viz-card__sub">{sub}</p>
        </div>
        <div className="viz-toggle" role="group" aria-label={`${title} view`}>
          <button
            className={`viz-toggle__btn${view === 'chart' ? ' viz-toggle__btn--on' : ''}`}
            aria-pressed={view === 'chart'}
            onClick={() => setView('chart')}
          >
            Chart
          </button>
          <button
            className={`viz-toggle__btn${view === 'table' ? ' viz-toggle__btn--on' : ''}`}
            aria-pressed={view === 'table'}
            onClick={() => setView('table')}
          >
            Table
          </button>
        </div>
      </div>
      {view === 'chart' ? children : table}
    </section>
  );
};

const VizTable: React.FC<{ head: string[]; rows: React.ReactNode[][] }> = ({ head, rows }) => (
  <div className="viz-table-wrap">
    <table className="viz-table">
      <thead>
        <tr>{head.map(h => <th key={h}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={head.length} className="viz-table__empty">Nothing recorded in this window.</td></tr>
        ) : rows.map((r, i) => (
          <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>
        ))}
      </tbody>
    </table>
  </div>
);
